import type { IPrice } from '@bridebook/models/source/models/Suppliers/Prices.types';
import { CountryCodes } from '../../gazetteer';

/**
 * Pure checks describing what kind of pricing a supplier offers.
 *
 * Shared by the couple-side price estimate tool and by server-side analytics, so both
 * report the same thing. Packages arrive flat from Firestore and grouped by year on the
 * client, so every helper takes a plain list - a package repeated across years does not
 * change any of the answers.
 */

// Packages sold on their own. Legacy packages have no flag and count as base ones.
export const isBasePackage = (price: IPrice) => price.standalone !== false;

/** Supplier has any pricing at all. */
export const hasPricing = (prices: IPrice[]) => prices.length > 0;

/** Every base package is approved for couples. */
export const isPricingVerified = (prices: IPrice[]) => {
  const basePrices = prices.filter(isBasePackage);

  return basePrices.length > 0 && basePrices.every((price) => price.verified);
};

/** Any package can be extended with paid options. */
export const isPricingModular = (prices: IPrice[]) =>
  prices.some((price) => (price.options?.length ?? 0) > 0);

/** Years the couple is offered: the current one and the next three. */
const YEARS_AHEAD = 3;

/** Year a pricing boundary falls in, read in UTC, the zone packages are filed under. */
const readYear = (timestamp: IPrice['active']['since']): number | undefined => {
  const date =
    timestamp?.toDate?.() ??
    (typeof timestamp?.seconds === 'number' ? new Date(timestamp.seconds * 1000) : undefined);

  return date?.getUTCFullYear();
};

/**
 * Packages a couple can reach, grouped by the year they apply to. A year the venue priced
 * nothing for borrows the closest year that has packages, nearest future first, and is
 * left out where the venue priced nothing anywhere near it.
 */
export const groupPricesByYear = (
  prices: IPrice[],
  now: Date = new Date(),
): Record<number, IPrice[]> => {
  const currentYear = now.getUTCFullYear();
  const byYear: Record<number, IPrice[]> = {};

  // Every package lands in each year it is sold in, as far out as a fallback can reach.
  for (const price of prices) {
    const since = readYear(price.active.since) ?? -Infinity;
    const until = readYear(price.active.until) ?? +Infinity;

    for (let year = currentYear - YEARS_AHEAD; year <= currentYear + YEARS_AHEAD; ++year) {
      if (year < since || year > until) continue;
      if (byYear[year] == null) byYear[year] = [];
      byYear[year].push(price);
    }
  }

  const offsets = [0, +1, -1, +2, -2, +3, -3];
  const result: Record<number, IPrice[]> = {};

  for (let year = currentYear; year <= currentYear + YEARS_AHEAD; ++year) {
    const packages = offsets
      .map((offset) => byYear[year + offset])
      .find((list) => list != null && list.length > 0);

    if (packages) result[year] = packages;
  }

  return result;
};

/**
 * Every package a couple can reach across the years on offer, once each. What the pricing
 * checks are meant to describe, so expired and far-off packages don't answer for what is
 * actually on the profile. Ordering follows the list given.
 */
export const getVisiblePrices = (prices: IPrice[], now: Date = new Date()): IPrice[] => {
  const byId = new Map<string, IPrice>();

  for (const price of Object.values(groupPricesByYear(prices, now)).flat()) {
    if (!byId.has(price.id)) byId.set(price.id, price);
  }

  return [...byId.values()];
};

/**
 * The pricing properties analytics reports alongside a supplier, derived from the packages
 * the couple can actually reach. Only GB venues take part in AI pricing, so everything else
 * reports it as off.
 */
export const getPricingAiProperties = ({
  countryCode,
  isVenue,
  prices,
}: {
  countryCode?: CountryCodes;
  isVenue: boolean;
  prices: IPrice[];
}) => {
  const visiblePrices = getVisiblePrices(prices);

  return {
    pricingAiEnabled: isVenue && countryCode === CountryCodes.GB && hasPricing(visiblePrices),
    pricingAiVerified: isPricingVerified(visiblePrices),
    pricingStructureModular: isPricingModular(visiblePrices),
  };
};
